/**
 * Recorder API functions
 */

import { apiClient } from "@/lib/api/client";
import { RecorderAnswerType } from "@speakai/shared";

// -- Types ------------------------------------------------------------

export interface RecorderDuration {
  minDuration: number;
  maxDuration: number;
}

export interface RecorderOptions {
  audio: boolean;
  video: boolean;
  screenShare: boolean;
  upload: {
    file: boolean;
    multiple: boolean;
    url: boolean;
  };
  liveTranscription: boolean;
  /** A public player link is created for each live recording. Only honoured with liveTranscription. */
  embedMediaPlayer?: boolean;
}

export interface RecorderNotification {
  upload: boolean;
  client: boolean;
  /** Server returns the notify-team list nested here, not at the top level. */
  notifyUsers?: string[];
}

export interface RecorderMeta {
  primaryColor?: string;
  fontColor?: string;
  fontFamily?: string;
  theme?: string;
  logo?: string;
  backgroundImg?: string;
  hideWaveform?: boolean;
  hideTitle?: boolean;
  hideDescription?: boolean;
  hideSubmitButton?: boolean;
  submitButtonLabel?: string;
  countdown?: boolean;
  hideImages?: boolean;
  customCSS?: string;
  /** Recording options live here on the server (audio/video/screenShare/upload/liveTranscription). */
  type?: RecorderOptions;
  client?: {
    name: boolean;
    email: boolean;
    questions: RecorderQuestion[];
    consent?: RecorderConsent;
  };
}

export interface RecorderQuestion {
  id?: string;
  fieldId?: string;
  question: string;
  isRequired: boolean;
  answerType: string;
  /** Choice list for Checkbox / Radiobutton / Dropdownlist answer types. */
  options?: string[];
  /** When true, the recorder UI appends an "Other" choice with a free-text field. */
  includeOther?: boolean;
  /** This question's answer names the submitted recording. At most one per recorder. */
  isTitle?: boolean;
}

export interface RecorderConsent {
  isEnabled: boolean;
  title: string;
  description: string;
  yesButtonLabel: string;
  noButtonLabel: string;
  isRequired: boolean;
  fieldId?: string;
}

const VALID_ANSWER_TYPES: ReadonlySet<string> = new Set(Object.values(RecorderAnswerType));

/**
 * Build the `clientInformation` create payload from a clone source's
 * `meta.client` block, so questions and consent carry over to the copy.
 * Questions are re-mapped field-by-field because the API response includes
 * Mongo `_id`s the create schema doesn't accept. Returns the standard
 * defaults (name/email on, no questions) when there is no clone source.
 *
 * A clone source saved before the answerType enum was tightened can still
 * carry a since-removed value (e.g. a legacy "text" type); resending that
 * verbatim fails the create schema with a 500 instead of a clean validation
 * error, so an unrecognized value falls back to Single here.
 */
export function clientInformationFromMetaClient(
  client?: RecorderMeta["client"]
): NonNullable<CreateRecorder["clientInformation"]> {
  if (!client) {
    return { name: true, email: true, questions: [] };
  }
  return {
    name: client.name ?? true,
    email: client.email ?? true,
    questions: (client.questions ?? []).map((q) => ({
      id: q.id,
      question: q.question,
      isRequired: q.isRequired ?? false,
      answerType: VALID_ANSWER_TYPES.has(q.answerType) ? q.answerType : RecorderAnswerType.Single,
      options: q.options ?? [],
      includeOther: q.includeOther ?? false,
      fieldId: q.fieldId ?? "",
    })),
    ...(client.consent
      ? {
          consent: {
            isEnabled: client.consent.isEnabled ?? false,
            title: client.consent.title ?? "",
            description: client.consent.description ?? "",
            yesButtonLabel: client.consent.yesButtonLabel ?? "Yes",
            noButtonLabel: client.consent.noButtonLabel ?? "No",
            isRequired: client.consent.isRequired ?? false,
            fieldId: client.consent.fieldId ?? "",
          },
        }
      : {}),
  };
}

export interface RecorderRecording {
  recorderId: string;
  mediaId: {
    mediaId: string;
    name: string;
    duration?: { inSecond: number };
    folderId?: string;
    meta?: { regionName?: string; countryName?: string };
    tags?: string[];
  };
  clientName?: string;
  clientEmail?: string;
  status?: string;
  manageBy?: {
    firstName?: string;
    lastName?: string;
  } | null;
  client?: {
    name?: string;
    email?: string;
    isConsent?: boolean;
    questions?: Array<{ id?: string; question: string; answer?: string; answerType: string }>;
  };
  createdAt: string;
}

export interface MediaRecordingQuestion {
  id?: string;
  question: string;
  answer?: string;
  answerType?: string;
}

/** Survey/recording details captured when a media was submitted via a recorder. */
export interface MediaRecording {
  client?: {
    name?: string;
    email?: string;
    isConsent?: boolean;
    questions?: MediaRecordingQuestion[];
  };
  recorderId: string;
}

/**
 * Survey details for a media that came from a recorder. The endpoint returns an
 * empty array (`[]`) when the media has no recording — callers normalize to null.
 */
export async function getMediaRecording(mediaId: string) {
  return apiClient.get<MediaRecording | unknown[]>(
    `/recorder/recording/${mediaId}`
  );
}

export interface RecorderData {
  recorderId: string;
  name: string;
  description: string;
  sourceLanguage: string;
  folderId: string;
  folderName?: string;
  isActive: boolean;
  isAutoAnalyze: boolean;
  notifyUsers: string[];
  token: string;
  domain?: string;
  duration: RecorderDuration;
  options: RecorderOptions;
  notification: RecorderNotification;
  meta: RecorderMeta;
  recordings?: RecorderRecording[];
  /** 6-digit pairing code for native recorder apps. Present only when the company enables pairing codes. */
  pairingCode?: string;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Shared fields accepted by both POST /recorder/create and
 * PUT /recorder/settings/:recorderId. Mirrors the server's
 * `updateSettings` Joi whitelist exactly.
 */
export interface UpdateRecorder {
  name: string;
  description: string;
  sourceLanguage: string;
  folderId: string;
  isAutoAnalyze: boolean;
  notifyUsers: string[];
  duration: RecorderDuration;
  options: RecorderOptions;
  notification: RecorderNotification;
  meta?: Omit<RecorderMeta, "client">;
}

/**
 * Create-only payload. Adds `clientInformation` which is whitelisted by the
 * server's `createRecorder` Joi schema (seeds `meta.client` defaults). The
 * `updateSettings` schema does NOT accept this field — sending it on PUT
 * would 400.
 */
export interface CreateRecorder extends UpdateRecorder {
  /**
   * Initial client-information defaults — only sent on POST /recorder/create
   * to seed the recorder's `meta.client` block. Mirrors Angular's
   * createRecorder payload which spreads the form's `clientInformation`
   * group (`{ name: boolean, email: boolean, questions: [] }`). Clone mode
   * sends the source recorder's questions and consent here so they carry
   * over to the copy.
   */
  clientInformation?: {
    name: boolean;
    email: boolean;
    questions: RecorderQuestion[];
    consent?: RecorderConsent;
  };
}

/** @deprecated Use `CreateRecorder` (POST) or `UpdateRecorder` (PUT). */
export type CreateUpdateRecorder = CreateRecorder;

export interface RecorderStatistics {
  totalSubmissions: number;
  averageRecordingTime: number;
  totalDuration: number;
  submissionsByDate: Array<{ date: string; count: number }>;
  submissionTypes: Record<string, number>;
  devices?: {
    types?: Record<string, number>;
    browsers?: Record<string, number>;
  };
  locations?: {
    cities?: Record<string, number>;
    countries?: Record<string, number>;
  };
  clientData?: Array<{ name?: string; email?: string; [question: string]: unknown }>;
}

export interface RecorderListFilters {
  page?: number;
  pageSize?: number;
  filterName?: string;
  sortBy?: string;
  sortDir?: string;
  from?: string;
  to?: string;
  folderIds?: string[];
  notifyUsers?: string[];
  isActive?: boolean;
}

// -- API Functions ----------------------------------------------------

/** List recorders with pagination and filters. */
export async function listRecorders(filters: RecorderListFilters = {}) {
  const {
    page = 0,
    pageSize = 50,
    filterName = "",
    sortBy = "createdAt",
    sortDir = "desc",
    from = "",
    to = "",
    folderIds = [],
    notifyUsers = [],
    isActive,
  } = filters;

  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sortBy: `${sortBy}:${sortDir}`,
  });
  if (filterName) params.set("filterName", filterName);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (folderIds.length) params.set("folderIds", folderIds.join(","));
  if (notifyUsers.length) params.set("notifyUsers", notifyUsers.join(","));
  if (isActive !== undefined) params.set("isActive", String(isActive));

  return apiClient.get<{ totalCount: number; recorderList: RecorderData[] }>(
    `/recorder?${params.toString()}`,
  );
}

/** Get recorder details by ID. */
export async function getRecorderDetails(recorderId: string) {
  return apiClient.get<RecorderData>(`/recorder/${recorderId}`);
}

/** Get recordings for a recorder with pagination. */
export async function getRecorderRecordings(
  recorderId: string,
  page: number = 0,
  pageSize: number = 25,
  filters: { filterName?: string; sortBy?: string; sortDir?: string } = {}
) {
  const { filterName = "", sortBy = "createdAt", sortDir = "desc" } = filters;
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sortBy: `${sortBy}:${sortDir}`,
  });
  if (filterName) params.set("filterName", filterName);

  return apiClient.get<{
    totalCount: number;
    recordings: RecorderRecording[];
  }>(
    `/recorder/recordings/${recorderId}?${params.toString()}`
  );
}

/** Create a new recorder. */
export async function createRecorder(data: CreateRecorder) {
  return apiClient.post<{
    recorderId: string;
    recorderData: RecorderData;
    token: string;
    domain?: string;
    pairingCode?: string;
  }>("/recorder/create", data);
}

/**
 * Generate or rotate the 6-digit pairing code for a recorder. Rotating
 * invalidates any device already paired with the previous code.
 */
export async function generatePairingCode(recorderId: string) {
  return apiClient.post<{ pairingCode: string }>(
    `/recorder/pairing-code/${recorderId}`,
    {},
  );
}

/**
 * Update recorder settings.
 *
 * Accepts `UpdateRecorder` (no `clientInformation`) because the server's
 * `updateSettings` Joi schema does NOT whitelist `clientInformation`.
 * Passing it would 400. Client-information defaults are seeded only at
 * create time via `createRecorder`.
 */
export async function updateRecorderSettings(
  recorderId: string,
  data: UpdateRecorder
) {
  return apiClient.put(`/recorder/settings/${recorderId}`, data);
}

/** Update recorder questions. */
export async function updateRecorderQuestions(
  recorderId: string,
  data: {
    name: boolean;
    email: boolean;
    questions: RecorderQuestion[];
    consent?: RecorderConsent;
  }
) {
  return apiClient.put(`/recorder/questions/${recorderId}`, data);
}

/** Add or edit a question. */
export async function addEditQuestion(
  recorderId: string,
  data: RecorderQuestion
) {
  return apiClient.post(`/recorder/questions/${recorderId}`, data);
}

/** Delete a question. */
export async function deleteQuestion(
  recorderId: string,
  questionId: string
) {
  return apiClient.delete(`/recorder/questions/${recorderId}/${questionId}`);
}

/** Update recorder status (activate/deactivate). */
export async function updateRecorderStatus(
  recorderIds: string[],
  isActive: boolean
) {
  return apiClient.put<{ status: string; message: string }>(
    "/recorder/status",
    { recorderIds, isActive }
  );
}

/** Delete a recorder. */
export async function deleteRecorder(recorderId: string) {
  return apiClient.delete(`/recorder/${recorderId}`);
}

/** Clone a recorder. */
export async function cloneRecorder(data: {
  recorderId: string;
  name: string;
  description: string;
  folderId: string;
}) {
  return apiClient.post("/recorder/clone", data);
}

/**
 * Bulk update payload — mirrors the server's `bulkUpdateRecorders` Joi
 * whitelist exactly. Only `folderId` and `notification` may change in bulk.
 * Status changes go through `updateRecorderStatus` (PUT /recorder/status).
 * Anything else MUST use the per-recorder `updateRecorderSettings` endpoint.
 */
export interface BulkUpdateRecordersPayload {
  recorderIds: string[];
  folderId?: string;
  notification?: {
    upload?: boolean;
    client?: boolean;
    notifyUsers?: string[];
  };
}

/** Bulk update recorders. */
export async function bulkUpdateRecorders(data: BulkUpdateRecordersPayload) {
  return apiClient.post("/recorder/bulk-update", data);
}

/** Get recorder statistics. */
export async function getRecorderStatistics(recorderId: string) {
  return apiClient.get<RecorderStatistics>(
    `/recorder/statistics/${recorderId}`
  );
}

/** Get submission summary. */
export async function getRecorderSubmissions(
  startDate?: string,
  endDate?: string
) {
  const params = new URLSearchParams();
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  const query = params.toString();

  return apiClient.get<{ receivedRecordings: number; totalRecorder: number }>(
    `/recorder/submissions${query ? `?${query}` : ""}`
  );
}

/** Export recorder data as CSV — triggers a direct browser download. */
export async function exportRecorder(
  recorderId: string,
  name: string
): Promise<{ success: boolean }> {
  try {
    const rawBlob = await apiClient.postBlob(`/recorder/export/${recorderId}`, null);
    const text = await rawBlob.text();
    const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = `${name}-export.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
    return { success: true };
  } catch {
    return { success: false };
  }
}

/** Download a recording media file. */
export async function downloadRecorderMedia(
  recorderId: string,
  mediaId: string
) {
  return apiClient.get<{ url: string }>(
    `/recorder/download/${recorderId}/${mediaId}`
  );
}

/** Upload meta image (logo/background). */
export async function uploadMetaImage(
  imageType: "logo" | "backgroundImg",
  recorderId: string,
  formData: FormData
) {
  return apiClient.upload(`/recorder/meta/${imageType}/${recorderId}`, formData);
}

/** Remove meta image. */
export async function removeMetaImage(
  imageType: "logo" | "backgroundImg",
  recorderId: string
) {
  return apiClient.delete(`/recorder/meta/${imageType}/${recorderId}`);
}

// -- Live Transcription -----------------------------------------------

/** Mint a short-lived live transcription handshake token for the authenticated user. */
export async function fetchLiveTranscriptionToken() {
  return apiClient.post<{ token: string; expiresIn: string }>(
    "/live-transcription/token",
    {}
  );
}
